/**
 * BetweenTestsPauseRow — the "Pause between tests" control shared by all
 * three run-template dialogs (Replay, Generation, Resume) (#1642).
 *
 * Inserts a pause after each test during the run so a rate-limited app
 * under test isn't overwhelmed by back-to-back specs — the between-tests
 * companion to the per-action slow-motion control. Surfaced in SECONDS to
 * match the sibling timeout rows; callers convert s→ms at save and ms→s at
 * init. The range starts at 0 (= no pause, the default) — unlike the
 * timeout rows, where 0 would mean "no timeout".
 *
 * Shared (not inlined per dialog) because all three dialogs render the same
 * control — the rule-of-three extraction so the label, help text, and clamp
 * can't drift across them.
 */
import { Info } from 'lucide-react'
import { Label, Input, Tooltip } from '@/components/ui'

export function BetweenTestsPauseRow({ value, onChange, align = 'start' }: {
  value: number
  onChange: (next: number) => void
  /** Which way the info tooltip opens. Pass 'end' (opens left) for a
   * right-column control so it doesn't run off the dialog's right edge;
   * 'start' (opens right) for a left-column control. */
  align?: 'start' | 'end'
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs flex items-center gap-1.5">
        Pause between tests (sec)
        <Tooltip
          content="Waits this long after each test before the next one starts. Use it when the app under test rate-limits or blocks rapid automation and a run hits 'too many requests'. 0 = no pause."
          triggerLabel="What is the pause between tests?"
          align={align}
        >
          <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        </Tooltip>
      </Label>
      <Input
        type="number"
        min={0}
        max={30}
        step={1}
        placeholder="0"
        value={value}
        onChange={e => {
          // Accept any non-negative integer during typing; the final
          // clamp to [0, 30] happens on blur so the "clear-then-type-a-
          // bigger-number" flow isn't snapped mid-edit.
          const parsed = parseInt(e.target.value, 10)
          onChange(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0)
        }}
        onBlur={() => onChange(Math.min(Math.max(value || 0, 0), 30))}
        className="w-28"
      />
    </div>
  )
}
